import { gql } from '@apollo/client';

/**
 * Shared contract for polling async rollup runs (executive analytics and
 * staff attribution). Both surfaces return the same GraphQL `BackfillRunStatus`
 * type, so a single fragment + a single polling hook
 * (`useBackfillRunStatusPolling`) serve both.
 *
 * Convention: every run-status query aliases its field to `runStatus` so the
 * hook can read `data.runStatus` regardless of the underlying resolver
 * (`backfillRunStatus` vs `staffAttributionRunStatus`).
 */
export const RUN_STATUS_FIELDS = gql`
  fragment RunStatusFields on BackfillRunStatus {
    runId
    status
    startedAt
    completedAt
    durationMs
    discoveredOrgs
    eligibleOrgs
    enqueuedJobs
    enqueueFailures
    completedJobs
    failedJobs
  }
`;

export type RunStatusState = 'running' | 'success' | 'partial_failed' | 'failed';

export interface BackfillRunStatus {
  runId: string;
  status: RunStatusState;
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  discoveredOrgs: number;
  eligibleOrgs: number;
  enqueuedJobs: number;
  /** Jobs that failed to enqueue during fan-out (coordinator/backfill side). */
  enqueueFailures: number;
  /** Worker jobs that have reached a terminal state (success or failure). */
  completedJobs: number;
  /**
   * Worker jobs that terminated in failure. The run-level `status` only
   * reflects fan-out success — workers run async — so a non-zero `failedJobs`
   * is the only signal that some rollups failed despite an all-jobs-done run.
   */
  failedJobs: number;
}

export interface RunStatusQueryData {
  runStatus: BackfillRunStatus | null;
}

export interface RunStatusQueryVars {
  runId: string;
}
